import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { CalendarClock, CheckCircle2, ClipboardList, Wind } from "lucide-react";
import { api, localDate } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const fmtDue = (iso) => (iso ? new Date(`${iso}T12:00:00`).toLocaleDateString(undefined, { month: "short", day: "numeric" }) : null);

export const AssignedSection = ({ compact = false }) => {
  const { isClient } = useAuth();
  const [items, setItems] = useState(null);

  useEffect(() => {
    if (!isClient) return;
    api.get("/assignments/mine", { params: { today: localDate() } }).then((r) => setItems(r.data)).catch(() => setItems([]));
  }, [isClient]);

  if (!isClient || !items || items.length === 0) return null;
  const open = items.filter((a) => a.status !== "done");
  const done = items.filter((a) => a.status === "done");

  return (
    <section className="rounded-3xl bg-indigo-600 text-white p-6 sm:p-8 relative overflow-hidden grain mb-10" data-testid="assigned-section">
      <ClipboardList className="absolute -right-6 -bottom-6 w-40 h-40 text-white/10" />
      <div className="relative">
        <p className="text-xs font-bold uppercase tracking-[0.2em] text-indigo-200">Assigned by your therapist</p>
        <h2 className="font-display text-xl sm:text-2xl font-bold mt-1">{open.length === 0 ? "All caught up — nicely done." : `${open.length} exercise${open.length === 1 ? "" : "s"} to practise`}</h2>
        <ul className={`mt-5 grid gap-3 ${compact ? "" : "sm:grid-cols-2"}`} data-testid="assigned-list">
          {(compact ? open.slice(0, 3) : items).map((a) => (
            <li key={a.id}>
              <Link to={`/tutorials/${a.tutorial_id}`} className={`block rounded-2xl p-4 transition-colors ${a.status === "done" ? "bg-white/10 hover:bg-white/15" : "bg-white text-slate-900 hover:bg-indigo-50"}`} data-testid={`assigned-item-${a.id}`}>
                <div className="flex items-start gap-3">
                  <span className={`w-9 h-9 rounded-xl grid place-items-center shrink-0 ${a.status === "done" ? "bg-emerald-400/30 text-emerald-200" : "bg-indigo-100 text-indigo-700"}`}>{a.status === "done" ? <CheckCircle2 className="w-4 h-4" /> : <Wind className="w-4 h-4" />}</span>
                  <div className="min-w-0 flex-1">
                    <p className="font-semibold leading-snug truncate">{a.tutorial.title}</p>
                    <p className={`text-xs mt-0.5 ${a.status === "done" ? "text-indigo-100" : "text-slate-500"}`}>
                      {a.tutorial.estimated_minutes} min{a.due_date ? ` · due ${fmtDue(a.due_date)}` : ""}
                      {a.status === "overdue" && <span className="ml-1 font-semibold text-rose-600">· overdue</span>}
                      {a.status === "done" && " · completed"}
                    </p>
                    {a.note && a.status !== "done" && <p className="text-xs text-slate-600 mt-1 italic">"{a.note}"</p>}
                  </div>
                  {a.due_date && a.status !== "done" && <CalendarClock className="w-4 h-4 text-slate-400 shrink-0" />}
                </div>
              </Link>
            </li>
          ))}
        </ul>
        {compact && done.length > 0 && <p className="text-xs text-indigo-200 mt-3">{done.length} completed · see all in <Link to="/my-learning" className="underline">My Learning</Link></p>}
      </div>
    </section>
  );
};
